import React, { useState, useEffect, useRef } from 'react';
import 'tailwindcss/tailwind.css';
import { FaAt } from 'react-icons/fa6';
import { CgCalendarDates } from 'react-icons/cg';
import { TbCircleArrowRight } from 'react-icons/tb';
import { VscListSelection } from 'react-icons/vsc';
import { FaUserCircle } from 'react-icons/fa';
import { FaHashtag } from 'react-icons/fa6';

import JiraIssueModal from './JiraIssueModal';

const JiraIssueTable = ({ issues = [] }) => {
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScaled, setIsScaled] = useState(false);

  const scaledRef = useRef(null);
  // NEW: State to track if the popup is active for blurring the background
  const [isPopupActive, setIsPopupActive] = useState(false);

  // Ref to hold the popup window instance
  const popupRef = useRef(null);
  // Ref to hold the interval ID for cleanup
  const intervalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scaledRef.current && !scaledRef.current.contains(event.target)) {
        setIsScaled(false);
      }
    };

    if (isScaled) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isScaled]);

  // This single useEffect manages all window interactions
  useEffect(() => {
    // This is the ONE focus handler for the component's lifetime
    const handleWindowFocus = () => {
      // If we have a reference to a popup and it's still open...
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
        popupRef.current = null; // Clear the ref after closing
        // MODIFIED: Remove blur when popup closes
        setIsPopupActive(false);
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    // This cleanup function runs when the component is removed
    return () => {
      window.removeEventListener('focus', handleWindowFocus);

      // Clean up any running interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Clean up any open popup
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []); // The empty array [] ensures this runs only ONCE. This is crucial.

  const closeModal = () => {
    setSelectedIssue(null);
    setIsModalOpen(false);
  };

  const openIssueInPopup = (issueUrl) => {
    if (!issueUrl) {
      alert('Could not open issue: URL not provided.');
      return;
    }
    // 1. Close the PREVIOUS popup if it's open
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }

    // 2. Clear any previous interval checker
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // 1. Define popup size
    // const popupWidthComp = window.innerWidth * 0.7;
    // const popupHeightComp = window.innerHeight * 0.7;
    // const popupWidthFixed = 800; // Fixed width for better consistency
    // const popupHeightFixed = 550; // Fixed height for better consistency

    // take max of both values popup heights and popup widths
    // const popupWidth = Math.max(popupWidthComp, popupWidthFixed);
    // const popupHeight = Math.max(popupHeightComp, popupHeightFixed);

    // 2. Correctly calculate the centering coordinates

    // This calculation is correct for horizontal centering
    // const left = window.screenX + (window.innerWidth / 2) - (popupWidth / 2);

    // // *** THE FIX: Account for the browser's top UI chrome ***
    // const chromeHeight = window.outerHeight - window.innerHeight;
    // const top = window.screenY + chromeHeight + (window.innerHeight / 2) - (popupHeight / 2);

    const popupWidth = 1000,
      popupHeight = 600;
    const left = window.screen.width / 2 - popupWidth / 2;
    const top = window.screen.height / 2 - popupHeight / 2;

    // 3. Set the features for the new window
    const popupFeatures = `
    width=${popupWidth},
    height=${popupHeight},
    top=${top},
    left=${left},
    resizable=yes,
    scrollbars=yes
  `;

    // 4. Open the new window
    // NEW: Store the new popup's reference in our ref
    popupRef.current = window.open(
      issueUrl,
      '_blank',
      popupFeatures.replace(/\s/g, ''),
    );

    // MODIFIED: If popup opens successfully, activate the blur
    if (popupRef.current) {
      setIsPopupActive(true);
    }

    // 4. Set an interval to check if the user closes the popup manually
    intervalRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearInterval(intervalRef.current);
        popupRef.current = null; // Clear the ref
        // MODIFIED: Remove blur when popup is manually closed
        setIsPopupActive(false);
      }
    }, 500);
  };

  if (!issues?.issues || issues.issues.length === 0) {
    return null;
  }

  return (
    <div className={`flex w-full items-center justify-start gap-2 pt-0`}>
      {/* <div className="h-10 w-10"></div> */}
      {/* <div className="max-h-[300px] flex w-[88%] rounded-xl items-center  overflow-clip justify-center mb-4"> */}
      <div className="no-scrollbar mb-4 flex max-h-[300px] w-full  items-center justify-center overflow-y-auto rounded-xl">
        {/* eslint-disable-next-line */}
        <div
          // onMouseEnter={() => setIsHovered(true)}
          // onMouseLeave={() => setIsHovered(false)}
          onClick={() => setIsScaled(true)}
          className={`no-scrollbar max-h-[300px] w-full transform items-center justify-center overflow-x-auto overflow-y-auto rounded-xl border border-gray-300  bg-white 
          shadow-md transition-all duration-300 ease-in-out 
          ${isScaled ? 'opacity-0' : 'opacity-100 '}
        `}
        >
          <table className="min-w-full table-auto text-left text-sm">
            <thead>
              <tr className="bg-zinc-100">
                <th className="px-3 py-2 font-medium text-gray-700">Type</th>
                <th className="px-3 py-2 font-medium text-gray-700">
                  <div className="flex items-center gap-2">
                    <FaHashtag />
                    Key
                  </div>
                </th>
                <th className="px-3 py-2 font-medium text-gray-700">
                  <div className="flex items-center gap-2">
                    <VscListSelection />
                    Summary
                  </div>
                </th>
                <th className="px-3 py-2 font-medium text-gray-700">
                  <div className="flex items-center gap-2">
                    <TbCircleArrowRight />
                    Status
                  </div>
                </th>
                <th className="px-3 py-2 font-medium text-gray-700">
                  <div className="flex items-center gap-2">
                    <FaAt className="text-gray-500" />
                    Assignee
                  </div>
                </th>
                <th className="px-3 py-2 font-medium text-gray-700">
                  <div className="flex items-center gap-2">
                    <CgCalendarDates className="text-gray-500" />
                    Due Date
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {issues.issues?.map((issue, index) => (
                <tr
                  key={issue.id}
                  className="cursor-pointer border-b border-gray-200 transition-colors duration-200 ease-in-out hover:bg-gray-50"
                  // onClick={() => openModal(issue)}
                  // Updated onClick to use the issue_url field from the data
                  onClick={() => {}}
                  // onClick={() => openIssueInPopup(issue?.issue_url)}
                >
                  <td className="border border-gray-200 px-3 py-2">
                    <div className="flex w-full justify-center">
                      <img
                        src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issue.fields?.issuetype?.name.toLowerCase()}.png`}
                        alt={`${issue.fields?.issuetype?.name} Icon`}
                        className="h-4 w-4"
                        onError={(e) =>
                          (e.target.src =
                            '/assets/jiraissuetable/jiraIssueTypeIcons/default.png')
                        }
                      />
                    </div>
                  </td>

                  <td className="whitespace-nowrap border border-gray-200 px-3 py-2 text-gray-800">
                    {issue.key}
                  </td>

                  <td className="whitespace-nowrap border border-gray-200 px-3 py-2 text-gray-800">
                    <div>
                      {' '}
                      {issue.fields?.summary?.length > 25
                        ? `${issue.fields?.summary?.substring(0, 24)}...`
                        : issue.fields?.summary}
                    </div>
                  </td>
                  <td className="whitespace-nowrap border border-gray-200 px-3 py-2">
                    <span
                      className={`rounded-xl px-2 py-1 text-xs font-semibold ${
                        issue.fields?.status?.name === 'To Do'
                          ? 'bg-blue-50 text-[#5652CF]'
                          : issue.fields?.status?.name === 'In Progress'
                            ? 'bg-yellow-50 text-yellow-500'
                            : 'bg-green-50 text-green-500'
                      }`}
                    >
                      {issue.fields?.status?.name}
                    </span>
                  </td>
                  <td className="whitespace-nowrap border border-gray-200 px-3 py-2">
                    {issue.fields?.assignee ? (
                      <div className="flex items-center">
                        <FaUserCircle className="mr-2 text-green-500" />
                        <span className="text-gray-700">
                          {issue.fields?.assignee?.displayName}
                        </span>
                      </div>
                    ) : (
                      <span className="italic text-gray-500">Unassigned</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap border border-gray-200 px-3 py-2 text-gray-500">
                    {issue.fields?.duedate
                      ? issue.fields?.duedate
                      : 'No Due Date'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isScaled && (
          <div
            className={`duration-600 fixed inset-0 z-50 mr-10 flex transform items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm transition-all ease-in-out  ${
              isPopupActive ? 'pointer-events-none blur-sm' : ''
            }`}
          >
            <div
              // onMouseEnter={() => setIsHovered(true)}
              // onMouseLeave={() => setIsHovered(false)}
              ref={scaledRef}
              className="flex h-[600px] w-[60%] items-center justify-center overflow-hidden rounded-xl "
            >
              <div className="scrollbar-none max-h-[600px] w-full items-center justify-center overflow-x-auto overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-md transition-all duration-300 ease-in-out">
                <table className="min-w-full table-auto text-left text-sm">
                  <thead>
                    <tr className="bg-zinc-100">
                      <th className="px-3 py-2 font-medium text-gray-700">
                        Type
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <FaHashtag />
                          Key
                        </div>
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <VscListSelection />
                          Summary
                        </div>
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <TbCircleArrowRight />
                          Status
                        </div>
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <FaAt className="text-gray-500" />
                          Assignee
                        </div>
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <CgCalendarDates className="text-gray-500" />
                          Due Date
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {issues.issues?.map((issue, index) => (
                      <tr
                        key={issue.id}
                        className="cursor-pointer border-b border-gray-200 transition-colors duration-200 ease-in-out hover:bg-gray-50"
                        // onClick={() => openModal(issue)}
                        // Updated onClick to use the issue_url field from the data
                        onClick={() => openIssueInPopup(issue?.issue_url)}
                      >
                        <td className="border border-gray-200 px-3 py-2">
                          <div className="flex w-full justify-center">
                            <img
                              src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issue.fields?.issuetype?.name.toLowerCase()}.png`}
                              alt={`${issue.fields?.issuetype?.name} Icon`}
                              className="h-4 w-4"
                              onError={(e) =>
                                (e.target.src =
                                  '/assets/jiraissuetable/jiraIssueTypeIcons/default.png')
                              }
                            />
                          </div>
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-3 py-2 text-gray-800">
                          {issue.key}
                        </td>

                        <td className="whitespace-nowrap border border-gray-200 px-3 py-2 text-gray-800">
                          <div>
                            {' '}
                            {issue.fields?.summary?.length > 25
                              ? `${issue.fields?.summary?.substring(0, 24)}...`
                              : issue.fields?.summary}
                          </div>
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-3 py-2">
                          <span
                            className={`rounded-xl px-2 py-1 text-xs font-semibold ${
                              issue.fields?.status?.name === 'To Do'
                                ? 'bg-blue-50 text-[#5652CF]'
                                : issue.fields?.status?.name === 'In Progress'
                                  ? 'bg-yellow-50 text-yellow-500'
                                  : 'bg-green-50 text-green-500'
                            }`}
                          >
                            {issue.fields?.status?.name}
                          </span>
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-3 py-2">
                          {issue.fields?.assignee ? (
                            <div className="flex items-center">
                              <FaUserCircle className="mr-2 text-green-500" />
                              <span className="text-gray-700">
                                {issue.fields?.assignee?.displayName}
                              </span>
                            </div>
                          ) : (
                            <span className="italic text-gray-500">
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap border border-gray-200 px-3 py-2 text-gray-500">
                          {issue.fields?.duedate
                            ? issue.fields?.duedate
                            : 'No Due Date'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {isModalOpen && (
          <JiraIssueModal issue_key={selectedIssue.key} onClose={closeModal} />
        )}
      </div>
    </div>
  );
};

export default JiraIssueTable;
